import { useRead, ReadState, isVideos } from "@/components/LoadingStates";
import { useAuth } from "@/context/AuthContext";
import { isLiveVideo } from "@/components/LiveDemoBadge";
import IncludeDemoToggle from "@/components/IncludeDemoToggle";
import useIncludeDemo from "@/hooks/useIncludeDemo";
import { useState, useMemo } from "react";
import SectionTabs, { SECTION_TABS } from "@/components/SectionTabs";

import VideoCard from "@/components/VideoCard";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Zap } from "lucide-react";
import { classNames } from "@/lib/format";
import ExportButton from "@/components/ExportButton";

const PLATFORMS = [
  { key: "all", label: "All" },
  { key: "instagram", label: "Instagram" },
  { key: "tiktok", label: "TikTok" },
  { key: "youtube", label: "Shorts" },
];

const SORTS = [
  { key: "outlier", label: "Multiplier" },
  { key: "views", label: "Views" },
  { key: "engagement", label: "Engagement" },
  { key: "recent", label: "Recent" },
];

export default function Outliers() {
  const { user } = useAuth();
  const [platform, setPlatform] = useState("all");
  const [sort, setSort] = useState("outlier");
  const [includeDemo, setIncludeDemo] = useIncludeDemo(user?.workspace_id);
  const videosRead = useRead(`/videos?outliers_only=true&platform=${platform}&sort=${sort}&limit=60`, isVideos);
  const rawVideos = useMemo(() => videosRead.data || [], [videosRead.data]);
  const videos = useMemo(() => rawVideos.filter((v) => includeDemo || isLiveVideo(v)), [rawVideos, includeDemo]);
  const hasFilters = platform !== "all" || !includeDemo;
  const summary = useMemo(() => {
    if (!videos.length) return null;
    const avg = videos.reduce((s, v) => s + v.outlier_multiplier, 0) / videos.length;
    const max = Math.max(...videos.map((v) => v.outlier_multiplier));
    return { avg: avg.toFixed(2), max: max.toFixed(1), count: videos.length };
  }, [videos]);

  return (
    <div className="space-y-6" data-testid="outliers-page">
      <SectionTabs tabs={SECTION_TABS.signals} activeKey="outliers" ariaLabel="Signals sections" data-testid="section-tabs-signals" />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Zap className="h-6 w-6 text-[hsl(var(--emerald))]" />
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Outlier Feed</h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            Vidéos à au moins 2,5× la baseline d’un profil suivi ; référence maximale indiquée sur chaque vidéo.
          </p>
        </div>
          <div className="flex flex-wrap items-center gap-3">
            <ExportButton path={`/export/videos.csv?outliers_only=true&platform=${platform}&sort=${sort}&limit=1000`} testid="export-outliers-button" />
          {summary && <Card className="p-3 flex items-center gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Videos</div>
              <div className="font-mono font-bold">{summary.count}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg lift</div>
              <div className="font-mono font-bold text-[hsl(var(--emerald))]">{summary.avg}x</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Peak</div>
              <div className="font-mono font-bold text-[hsl(var(--rose))]">{summary.max}x</div>
            </div>
          </Card>}
          </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          {PLATFORMS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPlatform(p.key)}
              data-testid={`platform-filter-${p.key}`}
              className={classNames(
                "px-3 py-1.5 text-xs rounded-md transition-colors font-medium",
                platform === p.key ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 rounded-lg bg-muted p-1 ml-auto">
          {SORTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSort(s.key)}
              data-testid={`sort-${s.key}`}
              className={classNames(
                "px-3 py-1.5 text-xs rounded-md transition-colors font-medium",
                sort === s.key ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <IncludeDemoToggle checked={includeDemo} onChange={setIncludeDemo} />
      <p className="text-xs text-muted-foreground" data-testid="demo-filter-scope">{videos.length} éléments affichés sur {rawVideos.length} reçus.</p>
      {!includeDemo && <p className="text-xs text-muted-foreground" data-testid="demo-export-scope">Le filtre démo/live agit sur l’affichage ; l’export CSV conserve le périmètre serveur.</p>}
      <ReadState read={videosRead} scope="outliers" empty={!videos.length} filtered={hasFilters} title={!includeDemo && rawVideos.length ? "Aucun contenu Live parmi les résultats reçus" : undefined} description="Modifiez les filtres ou consultez les créateurs pour trouver du contenu." primaryCta={hasFilters ? { label: "Effacer les filtres", onClick: () => { setPlatform("all"); setIncludeDemo(true); } } : { label: "Consulter les créateurs", to: "/creators" }}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {videos.map((v) => <VideoCard key={v.id} video={v} />)}
        </div>
      </ReadState>
    </div>
  );
}
